/**
 * Hinweis, dass sich die Bilderstrecke weiterscrollen laesst.
 *
 * Auf dem Handy stehen die Bilder zu zweit nebeneinander und laufen weit nach
 * unten - zu sehen ist beim Aufschlagen der Seite aber nur die erste Reihe.
 * Ein Punkt wischt deshalb einmal nach oben, so wie der Daumen es taete - in
 * der Mitte zwischen Header und Kauffenster, also mitten in dem, was von den
 * Bildern zu sehen ist.
 *
 * Dazu faehrt die Seite selbst ein kleines Stueck mit und wieder zurueck: der
 * Punkt zeigt die Geste, die Bewegung dahinter zeigt, was sie bewirkt.
 *
 * Gezeigt wird das nur einmal je Seitenaufruf, nur auf dem Handy und nur, wenn
 * unterhalb des Kauffensters ueberhaupt noch Bilder liegen. Wer selbst anfaengt,
 * hat den Hinweis nicht mehr noetig - dann bricht er sofort ab und die Seite
 * bleibt, wo der Finger sie hinbringt.
 */
const DAUER = 2200;

/** Wie weit die Seite dabei mitfaehrt - genug, um es zu sehen, wenig genug, um nicht zu stoeren. */
const WEG = 96;

/*
 * Dieselben Marken wie in den Keyframes von dp-hinweis-wisch: bei 18 Prozent
 * setzt sich der Punkt in Bewegung, bei 48 ist er oben, bei 78 wieder unten.
 * Die Seite folgt genau diesem Verlauf - deshalb stehen die Zahlen hier und
 * nicht als einzelne Zeitpunkte.
 */
/** Durchmesser des Punktes - siehe .dp-scrollhinweis__punkt im Stilblatt. */
const PUNKT = 26;

/**
 * Darf dieser Hinweis noch einmal gezeigt werden?
 *
 * Hoechstens zweimal je Besuch - danach kennt man ihn. Gezaehlt wird im
 * Sitzungsspeicher: er gilt fuer den Tab und ist beim naechsten Besuch wieder
 * leer. Dieselbe Zaehlung steht in dp-bild-overlay.js, dp-galerie-hinweis.js
 * und dp-scroller.js; jede fuehrt ihren eigenen Eintrag.
 *
 * Gezaehlt wird erst beim tatsaechlichen Zeigen, nicht beim Pruefen der
 * uebrigen Bedingungen.
 *
 * @returns {boolean}
 */
function darfHinweisZeigen() {
  const schluessel = 'dp-hinweis-galerie';

  try {
    const bisher = Number(window.sessionStorage.getItem(schluessel) || '0');
    if (bisher >= HINWEIS_HOECHSTENS) return false;

    window.sessionStorage.setItem(schluessel, String(bisher + 1));
    return true;
  } catch {
    /*
     * Im privaten Fenster ist der Speicher gesperrt. Dann eben ohne Zaehlung -
     * ein Hinweis ist kein Grund, hier auszusteigen.
     */
    return true;
  }
}

/** Wie oft ein Hinweis je Besuch hoechstens erscheint. */
const HINWEIS_HOECHSTENS = 2;


/*
 * Beruehrungen werden von Anfang an mitgeschrieben - nicht erst, wenn der
 * Hinweis laeuft. Wer kurz vor dem Start selbst zu scrollen beginnt, hat
 * seine Geste schon hinter sich, wenn die Abbruch-Horcher des Hinweises erst
 * angemeldet werden; die Schwungfahrt danach meldet keine Beruehrung mehr.
 * Deshalb zaehlt hier, ob gerade ein Finger aufliegt oder eben einer da war.
 */
let fingerLiegtAuf = false;
let letzteBeruehrung = 0;

window.addEventListener('touchstart', () => { fingerLiegtAuf = true; letzteBeruehrung = performance.now(); }, { passive: true });
window.addEventListener('touchmove', () => { letzteBeruehrung = performance.now(); }, { passive: true });
window.addEventListener('touchend', () => { fingerLiegtAuf = false; letzteBeruehrung = performance.now(); }, { passive: true });
window.addEventListener('touchcancel', () => { fingerLiegtAuf = false; letzteBeruehrung = performance.now(); }, { passive: true });
window.addEventListener('wheel', () => { letzteBeruehrung = performance.now(); }, { passive: true });

/** So lange nach der letzten Beruehrung faengt der Hinweis nicht an. */
const KARENZ = 1200;

const BEGINN = 0.18;
const OBEN = 0.48;
const ENDE = 0.78;

/**
 * Weiches An- und Auslaufen, wie das ease-in-out der Animation.
 *
 * @param {number} t Anteil zwischen 0 und 1.
 * @returns {number}
 */
/** Ob die Grossansicht der Produktbilder offen ist - dp-bild-overlay.js setzt dafuer data-dp-bildfeld. */
function bildfeldOffen() {
  return document.documentElement.dataset.dpBildfeld === 'offen';
}

function weich(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

function zeigeHinweis() {
  if (!window.matchMedia('(max-width: 749px)').matches) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (document.querySelector('.dp-scrollhinweis')) return;

  // Die Galerie des Artikels - nicht die Bilderreihe im Warenkorb (dp-korb-perso-galerie).
  const galerie = document.querySelector('media-gallery:not(.dp-korb-perso-galerie)');
  if (!galerie) return;

  /*
   * Eine leere Galerie (nur Bilder mit Farb-Marke, noch keine Farbe gewaehlt -
   * siehe dp-bildfilter) hat keine Bilder, zu denen man scrollen koennte.
   */
  if (galerie.classList.contains('dp-galerie--leer')) return;

  /*
   * Nur wenn unter dem Kauffenster noch Bilder liegen.
   *
   * Das Fenster schwebt ueber der Bilderstrecke; was darunter faellt, ist nicht
   * zu sehen, auch wenn es noch auf dem Bildschirm waere. Der Bildschirmrand
   * taugt deshalb nicht als Grenze - danach richtet sich nur, wo kein Fenster
   * da ist. Ein Artikel, dessen Bilder darueber enden, braucht den Hinweis
   * nicht.
   */
  const rand = galerie.getBoundingClientRect();
  const kauffenster = document.querySelector('.dp-kauffenster');
  const grenze = kauffenster
    ? kauffenster.getBoundingClientRect().top
    : window.innerHeight;

  if (rand.bottom <= grenze + 8) {
    /*
     * Frueher endete es hier: keine Bilder unter dem Fenster, kein Hinweis.
     * Das griff aber zu kurz, sobald das Kauffenster am Bildende parkt -
     * dann endet die Galerie immer genau an seiner Oberkante, und der
     * Hinweis kam nie, obwohl die Seite laengst weitergeht (Jimmi hat das
     * auf den Produktseiten mehrfach beanstandet). Jetzt wischt der Punkt
     * auch dann, wenn unterhalb zwar keine Bilder, aber noch nennenswert
     * Seite liegt.
     */
    const rest = document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
    if (rest < 200) return;
  }

  // Auch nicht, wenn die Seite gar nicht am Anfang steht.
  if (window.scrollY > 40) return;

  /*
   * Und nicht mitten in eine eigene Geste hinein: liegt der Finger auf oder
   * lag er eben noch, gehoert die Seite dem Besucher - eine Schwungfahrt kann
   * noch laufen, und zwei Kraefte am selben Scrollstand zittern nur. Der
   * Zaehler bleibt dabei unangetastet, der Hinweis darf spaeter noch kommen.
   */
  if (fingerLiegtAuf || performance.now() - letzteBeruehrung < KARENZ) {
    window.setTimeout(zeigeHinweis, KARENZ);
    return;
  }

  /*
   * Nicht nach einem Neuladen, das eine 3D-Datei aus dem Gedaechtnis
   * zurueckgebracht hat (3D-Vorlage, dp-3d-gedaechtnis.js setzt den Zettel):
   * wer seine Konfiguration gerade wiederfindet, soll nicht von der Seite
   * weggerollt werden. Der Zaehler bleibt unangetastet.
   */
  if (document.documentElement.dataset.dp3dWiederhergestellt !== undefined) return;

  /*
   * Und nicht, wenn das Bildoverlay offen ist (dp-bild-overlay.js setzt den
   * Zettel). War der Besucher schneller als der Hinweis und hat schon ein
   * Bild geoeffnet, rollte die Seite dahinter unsichtbar herunter und wieder
   * herauf. Wer die Bilder schon gefunden hat, braucht den Hinweis auch nach
   * dem Schliessen nicht mehr - der Zaehler bleibt unangetastet.
   */
  if (bildfeldOffen()) return;

  if (!darfHinweisZeigen()) return;

  const hinweis = document.createElement('span');
  hinweis.className = 'dp-scrollhinweis';
  hinweis.setAttribute('aria-hidden', 'true');
  hinweis.innerHTML = '<span class="dp-scrollhinweis__punkt"></span>';

  /*
   * Der Punkt steht mittig zwischen der Unterkante des Headers und der
   * Oberkante des Kauffensters - also mitten in dem Streifen, in dem
   * tatsaechlich Bilder zu sehen sind. Die Unterkante der Bilder taugt dafuer
   * nicht: sie liegt weit unterhalb des Bildschirms.
   */
  const header = document.querySelector('#header-component');
  const obenGrenze = header ? Math.max(header.getBoundingClientRect().bottom, 0) : 0;

  const untenGrenze = kauffenster
    ? Math.min(kauffenster.getBoundingClientRect().top, window.innerHeight)
    : Math.min(rand.bottom, window.innerHeight);

  /*
   * Der Punkt startet unterhalb der Mitte und endet oberhalb - die Bewegung
   * als Ganzes liegt damit mittig im Streifen. Stuende er in Ruhe schon in der
   * Mitte, liefe er nur nach oben weg und wirkte zu hoch.
   *
   * Gerechnet wird auf die Oberkante des Punktes, deshalb der Abzug seines
   * halben Durchmessers.
   */
  const mitte = (obenGrenze + untenGrenze) / 2;
  const ruhe = Math.round(mitte + WEG / 2 - PUNKT / 2);
  hinweis.style.setProperty('--dp-hinweis-oben', ruhe + 'px');

  /*
   * Waagrecht ueber der linken Bildspalte, nicht in der Mitte: dort laeuft die
   * Rinne zwischen den beiden Spalten, und der Punkt saesse auf weissem Grund
   * statt auf einem Bild.
   */
  // Nicht die breite Live-Vorschau der 3D-Vorlage (dp-3d-galerie): sie spannt beide Spalten, der Punkt saesse mittig.
  // Die erste sichtbare - vom Farbfilter verborgene Kacheln haben kein Mass.
  const zelle = [...galerie.querySelectorAll('.media-gallery__grid .product-media-container:not(.dp-3d-galerie)')].find(
    (kachel) => kachel instanceof HTMLElement && kachel.style.display !== 'none'
  );
  if (zelle) {
    const z = zelle.getBoundingClientRect();
    hinweis.style.setProperty('--dp-hinweis-links', Math.round(z.left + z.width / 2) + 'px');
  }

  document.body.appendChild(hinweis);

  /*
   * Die Bilder laufen ein Stueck mit.
   *
   * Der Punkt allein zeigt die Geste, die Bewegung dahinter zeigt, was sie
   * bewirkt. Beides zusammen ist in dem Moment zu sehen, in dem der Punkt nach
   * oben wischt - danach faehrt die Seite wieder an ihren Platz.
   */
  let abgebrochen = false;

  /*
   * Die Seite folgt dem Punkt Bild fuer Bild.
   *
   * Vorher wurde sie an zwei Zeitpunkten losgeschickt und lief dann im Tempo
   * des Browsers - der hat seine eigene Dauer und seine eigene Kurve, und
   * schon liefen Punkt und Bilder auseinander. Jetzt wird jeder einzelne
   * Schritt selbst gesetzt, mit demselben Verlauf wie die Animation.
   *
   * Das weiche Scrollen des Stilblattes muss dafuer kurz weichen, sonst legte
   * der Browser ueber jeden Schritt noch seine eigene Bewegung.
   */
  const wurzel = document.documentElement;
  const altesVerhalten = wurzel.style.scrollBehavior;
  wurzel.style.scrollBehavior = 'auto';

  const punkt = hinweis.querySelector('.dp-scrollhinweis__punkt');

  /*
   * Der Stand, den der letzte eigene Schritt gesetzt hat. Weicht die Seite
   * davon ab, scrollt jemand anderes - eine Schwungfahrt, ein spaeter Finger,
   * der Browser selbst. Dann hoert der Hinweis sofort auf, statt gegen die
   * fremde Bewegung anzusetzen: genau dieses Gegeneinander war als Zittern zu
   * sehen.
   */
  let erwartet = window.scrollY;

  /*
   * Die Bewegung laeuft auf einer eigenen Uhr, nicht auf der rohen Zeit.
   *
   * Bleibt der Hauptstrang haengen - auf dem iPhone etwa, wenn die frisch
   * gezeichneten Galeriebilder entschluesselt werden und ihr Blur-Uebergang
   * anlaeuft -, dann pausieren die Animationsbilder. Mit der rohen Zeit
   * spraenge t nach dem Haenger ueber das Ende hinaus, und die Seite
   * schnappte mitten in der Geste auf null zurueck: genau der Abbruch, den
   * Jimmi auf iOS gesehen hat. Die eigene Uhr rueckt je Bild hoechstens um
   * eine Handvoll Millisekunden vor - nach einem Haenger laeuft die Geste
   * einfach dort weiter, wo sie stand.
   */
  let uhr = 0;
  let zuletzt = performance.now();

  const schritt = (jetzt) => {
    if (abgebrochen) return;

    // Jemand anderes scrollt - oder das Bildoverlay ist aufgegangen, ohne dass ein Finger es beendet hat.
    if (Math.abs(window.scrollY - erwartet) > 2 || bildfeldOffen()) {
      beenden();
      return;
    }

    uhr += Math.min(jetzt - zuletzt, 90);
    zuletzt = jetzt;
    const t = uhr / DAUER;

    if (t >= ENDE) {
      window.scrollTo(0, 0);
      punkt.style.transform = 'translateY(0)';
      wurzel.style.scrollBehavior = altesVerhalten;
      /*
       * Aufgeraeumt wird erst jetzt, vom tatsaechlichen Ende aus - der Punkt
       * bleibt denselben Moment stehen wie bisher. Ein fester Wecker ab dem
       * Start passte nicht mehr: dauert die Geste wegen eines Haengers
       * laenger, fiele er mitten in die Fahrt.
       */
      window.setTimeout(beenden, DAUER * (1 - ENDE));
      return;
    }

    let weite = 0;
    if (t > BEGINN && t <= OBEN) {
      weite = WEG * weich((t - BEGINN) / (OBEN - BEGINN));
    } else if (t > OBEN) {
      weite = WEG * (1 - weich((t - OBEN) / (ENDE - OBEN)));
    }

    /*
     * Derselbe Wert fuer beides: der Punkt geht um genau die Strecke nach
     * oben, um die die Seite nach unten faehrt.
     */
    erwartet = Math.round(weite);
    window.scrollTo(0, erwartet);
    punkt.style.transform = 'translateY(' + -weite.toFixed(1) + 'px)';

    window.requestAnimationFrame(schritt);
  };

  window.requestAnimationFrame(schritt);

  function beenden() {
    abgebrochen = true;
    document.documentElement.style.scrollBehavior = '';
    hinweis.remove();

    for (const art of ['touchstart', 'touchmove', 'wheel', 'pointerdown', 'keydown']) {
      window.removeEventListener(art, beenden);
    }
  }

  /*
   * Beendet wird beim ersten eigenen Zutun - nicht beim Scrollen selbst: die
   * Seite bewegt sich hier ja auch von allein, und das wuerde den Hinweis
   * sofort wieder abraeumen.
   */
  for (const art of ['touchstart', 'touchmove', 'wheel', 'pointerdown', 'keydown']) {
    window.addEventListener(art, beenden, { passive: true, once: true });
  }

  /*
   * Nur noch die Reissleine, falls die Animationsbilder ganz ausbleiben -
   * das planmaessige Aufraeumen uebernimmt der letzte Schritt selbst.
   */
  window.setTimeout(beenden, DAUER * 3);
}

/**
 * Wartet, bis die sichtbaren Bilder fertig entschluesselt sind.
 *
 * Geladen heisst noch nicht gezeichnet: der Browser rechnet die Bilder danach
 * noch in die Anzeige um, und genau dabei bleibt eine Bewegung haengen. Bilder
 * ohne Unterstuetzung fuer decode() werden uebersprungen.
 *
 * @returns {Promise<void>}
 */
function warteAufBilder() {
  const bilder = [...document.querySelectorAll('media-gallery img')].filter((bild) => {
    const rand = bild.getBoundingClientRect();
    return rand.bottom > 0 && rand.top < window.innerHeight;
  });

  return Promise.all(
    bilder.map((bild) => (bild.decode ? bild.decode().catch(() => {}) : Promise.resolve()))
  ).then(() => undefined);
}

/**
 * Wartet auf eine ruhige Minute im Hauptstrang.
 *
 * Nach dem Laden arbeitet der Browser noch an Schriften, Bildern und den
 * Skripten der Apps. Faengt die Bewegung mittendrin an, ruckelt sie. Wo
 * requestIdleCallback fehlt, tut es eine kurze Pause.
 *
 * @returns {Promise<void>}
 */
function warteAufRuhe() {
  return new Promise((fertig) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => fertig(), { timeout: 1500 });
    } else {
      window.setTimeout(fertig, 400);
    }
  });
}

/**
 * Nachlauf, der sich nach der Ladezeit richtet.
 *
 * Kam die Seite aus dem Zwischenspeicher, steht sie fast sofort; musste sie
 * vollstaendig geladen werden, arbeitet der Browser danach noch eine Weile -
 * Schriften, Bilder, Skripte der Apps. In dem Moment zu wischen sieht
 * abgehackt aus. Der Nachlauf waechst deshalb mit der Ladezeit: ein Viertel
 * davon, mindestens gut eine halbe, hoechstens zwei Sekunden.
 *
 * @returns {number} Millisekunden.
 */
function nachlauf() {
  const eintrag = performance.getEntriesByType('navigation')[0];
  const ladezeit = eintrag ? eintrag.loadEventEnd - eintrag.startTime : 0;

  return Math.min(2000, Math.max(600, Math.round(ladezeit * 0.25)));
}

/*
 * Gestartet wird erst, wenn die Seite wirklich fertig ist: alle Dateien
 * geladen, die sichtbaren Bilder entschluesselt, der Hauptstrang frei - und
 * danach noch der Nachlauf. Vorher stimmt auch die Rechnung nicht, ob
 * unterhalb des Bildschirms noch Bilder liegen.
 */
function starteWennRuhe() {
  warteAufBilder()
    .then(warteAufRuhe)
    .then(() => new Promise((fertig) => window.setTimeout(fertig, nachlauf())))
    .then(zeigeHinweis)
    .catch(() => {
      /* Ein Hinweis ist kein Grund, etwas kaputtgehen zu lassen. */
    });
}

if (document.readyState === 'complete') {
  starteWennRuhe();
} else {
  window.addEventListener('load', starteWennRuhe, { once: true });
}
